import { useState } from 'react'

import omit from '@utils/omit'

const useForm = (defaultForm, validations = {}) => {
  const [values, setValues] = useState(defaultForm)
  const [errors, setErrors] = useState({})
  const [shouldValidate, setShouldValidate] = useState(false)

  const validationError = (name) => {
    const failedValidation = (validations[name] || []).find((validation) => {
      if (validation.conditional) {
        return validation.conditional(values) && !validation.validator(values[name])
      }

      return !validation.validator(values[name])
    })

    return failedValidation?.message || null
  }

  const validateAll = () => {
    const currentErrors = {}
    let isValid = true

    Object.keys(validations).forEach((name) => {
      const error = validationError(name)
      if (error) {
        currentErrors[name] = error
        isValid = false
      }
    })

    setErrors(currentErrors)
    return isValid
  }

  const validate = (name, value) => {
    const error = validationError(name, value)

    if (error) {
      setErrors({ ...errors, [name]: error })
      return false
    }

    setErrors(omit(errors, name))
    return true
  }

  const checkboxProps = (name) => ({
    id: name,
    checked: values[name],
    error: !!errors[name],
    helperText: errors[name],
    onChange: ({ target }) => {
      setValues({ ...values, [name]: target.checked })
      if (shouldValidate) {
        validate(name, target.checked)
      }
    },
  })

  const textFieldProps = (name) => ({
    id: name,
    value: values[name],
    error: !!errors[name],
    helperText: errors[name],
    onChange: ({ target }) => {
      setValues({ ...values, [name]: target.value })
      if (shouldValidate) {
        validate(name, target.value)
      }
    },
  })

  const selectProps = (name) => ({
    id: name,
    value: values[name],
    error: !!errors[name],
    helperText: errors[name],
    onChange: ({ target }) => {
      setValues({ ...values, [name]: target.value })
      if (shouldValidate) {
        validate(name, target.value)
      }
    },
  })

  const handleFormSubmit = (onSuccess) => (event) => {
    event.preventDefault()

    if (validateAll()) {
      onSuccess(values)
    } else if (!shouldValidate) {
      setShouldValidate(true)
    }
  }

  const resetForm = () => setValues(defaultForm)

  return {
    checkboxProps,
    textFieldProps,
    handleFormSubmit,
    selectProps,
    values,
    resetForm,
  }
}

export default useForm
